import classNames from "classnames";
import _ from "lodash";
import { connect } from "react-redux";

import { Select } from "@/components";
import type { Light, LightFixtureType } from "@/models";
import type { FloorSpaceCatalog } from "@/models/floor-space-catalog";
import type { LightGroupModel } from "@/types/entity-types";
import type { LuminaireType } from "@/types/map-types";

interface OverviewProps {
    lights: Light[];
    changeLights: (key: string, value: unknown) => Light[];
    persistLights: (lights?: Light[]) => void;
    groups: Record<string, LightGroupModel[]>;
    floorSpaceCatalogMap: Record<string, FloorSpaceCatalog>;
    lightTypes: LuminaireType[];
}

const Overview = ({
    lights: lightsProp,
    changeLights,
    persistLights,
    groups,
    floorSpaceCatalogMap,
    lightTypes,
}: OverviewProps) => {
    const getLights = () => {
        const lights = lightsProp;

        for (const light of lights) {
            const controlGroupId = light.options?.controlGroupId;

            if (!controlGroupId) {
                continue;
            }

            const group = groups[controlGroupId]?.[0];

            if (!group) {
                continue;
            }

            // eslint-disable-next-line react-hooks/immutability
            light.group = `${group.groupName} (${group.displayID})`;
        }

        return lights;
    };

    const defaultOptions = {
        defaultValue: "",
        differValue: "Multiple",
        handleSameValue: false,
    };

    const lights = getLights();
    const checkValue = (v: unknown, d: string): string => (v ? String(v) : d);

    const getValue = (
        value: string,
        options?: Partial<typeof defaultOptions>,
    ): string => {
        const { defaultValue, differValue, handleSameValue } = {
            ...defaultOptions,
            ...options,
        };

        const lightValue = _.get(lights[0], value);

        if (lights.length === 1 || !handleSameValue) {
            return lights.length === 1
                ? checkValue(lightValue, defaultValue)
                : differValue;
        }

        const sameValue = !!_.every(
            lights,
            (light) => _.get(light, value) === lightValue,
        );

        return sameValue ? checkValue(lightValue, defaultValue) : differValue;
    };

    const infoOptions = { defaultValue: " - " };

    const selectableTypes = _.sortBy(
        _.filter(
            lightTypes,
            (lightType) =>
                !!lightType.exteriorWidth && !!lightType.exteriorLength,
        ),
        "displayName",
    );

    const typeOptions = selectableTypes.map((lightType) => ({
        label: lightType.displayName,
        value: lightType.luminaireTypeID,
    }));

    const hasSingleType = _.every(
        lights,
        (light) => light.type?.id === lights[0].type?.id,
    );

    const selectedTypeId = hasSingleType ? lights[0]?.type?.id : undefined;

    const onChangeType = (luminaireTypeID: number) => {
        const lightType = _.find(selectableTypes, { luminaireTypeID });

        if (!lightType) {
            return;
        }

        const nextType: LightFixtureType = {
            id: lightType.luminaireTypeID,
            name: lightType.displayName,
            width: parseFloat(lightType.exteriorWidth),
            length: parseFloat(lightType.exteriorLength),
            interiorWidth: lightType.interiorWidth,
            interiorLength: lightType.interiorLength,
            isCLS: lightType.reportingType === "cls",
        };

        persistLights(changeLights("fixtureType", nextType));
    };

    const info = {
        group: getValue("group", { ...infoOptions, handleSameValue: true }),
        level: " - ",
        vlcId: getValue("id"),
        bleId: getValue("bleId", infoOptions),
        ciCode: getValue("ciCode", infoOptions),
        locationDescription: getValue("locationDescription"),
        firmware: getFirmware(lights, floorSpaceCatalogMap),
        flags:
            lights.length === 1
                ? checkValue(
                      lights[0].flags.map((flag) => flag.label).join(", "),
                      infoOptions.defaultValue,
                  )
                : defaultOptions.differValue,
    };

    return (
        <div className="col tab overview">
            <div className="group">
                <div className="group-title">Status</div>
                <div key="light-group">
                    Light group: <span>{info.group}</span>
                </div>
                <div key="light-level">
                    Light Level: <span>{info.level}</span>
                </div>
                <div key="flags">
                    Active Flags: <span>{info.flags}</span>
                </div>
            </div>
            <div className="group">
                <div className="group-title">Fixture Details</div>
                <div key="type" className="type-select">
                    <span className="type-select-label">Type:</span>
                    <Select
                        searchable
                        placeholder={
                            hasSingleType
                                ? "Select a fixture type…"
                                : "Multiple"
                        }
                        options={typeOptions}
                        value={selectedTypeId}
                        onChange={(option) =>
                            option &&
                            "value" in option &&
                            onChangeType(Number(option.value))
                        }
                    />
                </div>
                <div
                    key="vlc-id"
                    className={classNames({ ["no-value"]: !info.vlcId })}
                >
                    VLC ID:
                    <span>{info.vlcId ? info.vlcId : "No VLC ID"}</span>
                </div>
                <div key="ble-id">
                    BLE ID: <span>{info.bleId}</span>
                </div>
                <div key="ci-code">
                    CI Code: <span>{info.ciCode}</span>
                </div>
                <div key="firmware">
                    Firmware: <span>{info.firmware}</span>
                </div>
                <div
                    key="location-description"
                    className={classNames({
                        ["no-value"]: !info.locationDescription,
                    })}
                >
                    Location:
                    <span>
                        {info.locationDescription
                            ? info.locationDescription
                            : "Missing Location Description"}
                    </span>
                </div>
            </div>
            <div className="group notes">
                <div className="group-title">Notes</div>
                <textarea
                    placeholder="Type notes here..."
                    disabled={lights.length > 1}
                    value={getValue("notes")}
                    onBlur={() => persistLights()}
                    onChange={({ target }) =>
                        changeLights("notes", target.value)
                    }
                />
            </div>
        </div>
    );
};

const getFirmware = (
    lights: Light[],
    floorSpaceCatalogMap: Record<string, FloorSpaceCatalog>,
) => {
    if (lights.length === 1) {
        const catalogId = lights[0].options?.floorSpaceCatalog_ID;
        const catalog = catalogId ? floorSpaceCatalogMap[catalogId] : null;
        return catalog?.firmwareVersion ?? " - ";
    }

    const versions = lights.map((light) => {
        const catalogId = light.options?.floorSpaceCatalog_ID;
        const catalog = catalogId ? floorSpaceCatalogMap[catalogId] : null;
        return catalog?.firmwareVersion ?? null;
    });

    const first = versions[0];

    return versions.every((v) => v === first) ? (first ?? " - ") : "Multiple";
};

interface MapState {
    map: {
        general: {
            lightGroups: (LightGroupModel | null)[];
            floorSpaceCatalogMap: Record<string, FloorSpaceCatalog>;
            lightTypes: LuminaireType[];
        };
    };
}

const mapStateToProps = ({ map }: MapState) => ({
    groups: _.groupBy(
        map.general.lightGroups.filter((group) => !_.isNil(group)),
        "groupID",
    ),
    floorSpaceCatalogMap: map.general.floorSpaceCatalogMap,
    lightTypes: map.general.lightTypes,
});

const mapDispatchToProps = () => ({});

export default connect(mapStateToProps, mapDispatchToProps)(Overview);
